import { useEffect, useMemo, useState } from "react";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import PageHero from "@/components/base/PageHero";
import NewsCard from "@/components/base/NewsCard";
import Pagination from "@/components/base/Pagination";
import { filterNewsByCategory, newsCategories, newsList, sortNews } from "@/utils/anime";
import { usePageMeta } from "@/hooks/usePageMeta";

const PAGE_SIZE = 6;

const sortOptions = [
  { key: "latest", label: "最新发布", icon: "ri-time-line" },
  { key: "hot", label: "最热阅读", icon: "ri-fire-line" },
] as const;

export default function NewsPage() {
  usePageMeta(
    "动漫资讯 - 78动漫",
    "78动漫动漫资讯栏目，汇总最新 TV 动画、剧场版与漫画新闻，支持按发布时间与动漫类别筛选浏览。",
    "动漫资讯,动画新闻,剧场版动态,漫画新闻,78动漫",
  );

  const [sort, setSort] = useState<"latest" | "hot">("latest");
  const [category, setCategory] = useState("全部");
  const [page, setPage] = useState(1);
  const [filterOpen, setFilterOpen] = useState(false);

  const filtered = useMemo(() => {
    const sorted = sortNews(newsList, sort);
    return filterNewsByCategory(sorted, category);
  }, [sort, category]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  useEffect(() => {
    setPage(1);
  }, [sort, category]);

  const handlePageChange = (next: number) => {
    setPage(next);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <SiteHeader />
      <main className="flex-1">
        <PageHero
          icon="ri-newspaper-line"
          title="动漫资讯"
          subtitle="最新动漫资讯及动态，覆盖 TV 动画、剧场版与漫画新闻，按时间或热度浏览，快速掌握行业脉搏。"
          breadcrumb={[{ label: "动漫资讯" }]}
        />

        <section className="mx-auto max-w-7xl px-4 py-10 md:px-8 md:py-12">
          <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-foreground-700">排序</span>
                <div className="flex items-center gap-1 rounded-full bg-background-100 p-1">
                  {sortOptions.map((option) => (
                    <button
                      key={option.key}
                      type="button"
                      onClick={() => setSort(option.key)}
                      className={`flex items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-1.5 text-sm font-medium transition-colors cursor-pointer ${
                        sort === option.key
                          ? "bg-primary-500 text-background-50"
                          : "text-foreground-600 hover:text-foreground-950"
                      }`}
                    >
                      <i className={option.icon} />
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              <button
                type="button"
                onClick={() => setFilterOpen((prev) => !prev)}
                className="flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600 md:hidden cursor-pointer"
              >
                <i className="ri-filter-3-line" />
                类别筛选
                <i className={filterOpen ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} />
              </button>
            </div>

            <div className={`${filterOpen ? "flex" : "hidden"} mt-4 flex-wrap items-center gap-2 md:mt-4 md:flex`}>
              <span className="text-sm font-medium text-foreground-700">类别</span>
              {newsCategories.map((item) => (
                <button
                  key={item}
                  type="button"
                  onClick={() => setCategory(item)}
                  className={`whitespace-nowrap rounded-full px-3 py-1.5 text-sm transition-colors cursor-pointer ${
                    category === item
                      ? "bg-secondary-500 text-background-50"
                      : "bg-secondary-100 text-secondary-800 hover:bg-secondary-200"
                  }`}
                >
                  {item}
                </button>
              ))}
            </div>
          </div>

          <p className="mt-6 text-sm text-foreground-500">
            共找到 <strong className="font-semibold text-foreground-900">{filtered.length}</strong> 条资讯
            {category !== "全部" && ` · 类别：${category}`}
          </p>

          {pageItems.length > 0 ? (
            <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {pageItems.map((news) => (
                <NewsCard key={news.id} news={news} />
              ))}
            </div>
          ) : (
            <div className="mt-10 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100 py-16 text-center">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-foreground-500">
                <i className="ri-inbox-line text-2xl" />
              </span>
              <p className="mt-4 text-sm text-foreground-600">该分类下暂时没有资讯，换个类别看看吧。</p>
            </div>
          )}

          <Pagination current={page} total={totalPages} onChange={handlePageChange} />
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}